import React from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';

import Button from '../../components/Button';
import TextInput from '../../components/TextInput';
import '../Login/Login.css';

// Forced password change for a logged-in user whose account is flagged
// must_change_password — a temp-password account created when email is off, or one
// an admin reset. Session-authed (no token): the login cookie is already set, and
// the Login screen routes here instead of the dashboard when the flag is set.
// Mirrors app/core/password_policy.py for instant feedback; the backend re-checks.
const strongEnough = (pw) =>
	pw.length >= 12 &&
	/[a-z]/.test(pw) && /[A-Z]/.test(pw) && /\d/.test(pw) &&
	/[!@#$%^&*()\-_=+[\]{};:,.<>?/|\\~`"']/.test(pw);

const ChangePasswordScreen = () => {
	const navigate = useNavigate();
	const [current, setCurrent] = React.useState({ value: '', error: '' });
	const [password, setPassword] = React.useState({ value: '', error: '' });
	const [confirm, setConfirm] = React.useState({ value: '', error: '' });
	const [submitting, setSubmitting] = React.useState(false);

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!current.value) {
			setCurrent({ ...current, error: 'Enter your current (temporary) password.' });
			return;
		}
		if (!strongEnough(password.value)) {
			setPassword({ ...password, error: 'At least 12 characters with upper, lower, a digit and a special character.' });
			return;
		}
		if (confirm.value !== password.value) {
			setConfirm({ ...confirm, error: 'Passwords do not match.' });
			return;
		}
		setSubmitting(true);
		try {
			await axios.post(
				'/api/auth/change-password',
				{ current_password: current.value, new_password: password.value },
				{ withCredentials: true }
			);
			toast.success('Password updated.');
			navigate('/', { replace: true });
		} catch (err) {
			const msg = err?.response?.data?.detail || 'Could not change your password.';
			setPassword({ ...password, error: typeof msg === 'string' ? msg : 'Could not change your password.' });
			setSubmitting(false);
		}
	};

	return (
		<div className='loginContainer' style={{ backgroundImage: 'url(/assets/login-bg.jpg)' }}>
			<div className='blue_container' />
			<div className='login_form_container'>
				<div className='login_form_content'>
					<div className='login_form'>
						<h1 className='login_form_heading'>Change your password</h1>
						<form onSubmit={handleSubmit}>
							<p className='auth_helper_text'>
								Your account uses a temporary password. Choose a new one to continue.
							</p>
							<TextInput
								type='password'
								label='Current password'
								icon='lock'
								placeholder='Current (temporary) password'
								value={current.value}
								autoFocus
								onChange={(ev) => setCurrent({ value: ev.target.value })}
								errorMessage={current.error}
							/>
							<TextInput
								type='password'
								label='New password'
								icon='lock'
								placeholder='New password'
								value={password.value}
								onChange={(ev) => setPassword({ value: ev.target.value })}
								errorMessage={password.error}
							/>
							<TextInput
								type='password'
								label='Confirm password'
								icon='lock'
								placeholder='Confirm password'
								value={confirm.value}
								onChange={(ev) => setConfirm({ value: ev.target.value })}
								onKeyDown={(ev) => { if (ev.key === 'Enter') handleSubmit(ev); }}
								errorMessage={confirm.error}
							/>
							<Button
								type='submit'
								label={submitting ? 'Saving…' : 'Update password'}
								disabled={submitting}
								style={{ alignSelf: 'flex-start' }}
								onClick={handleSubmit}
							/>
						</form>
					</div>

					<div className='logo_container'>
						<img src='/assets/logo-new.png' alt='Frontline Logo' />
					</div>
				</div>
				<p className='powered_container'>
					Powered By
					<span>Watcher</span>
				</p>
			</div>
		</div>
	);
};

export default ChangePasswordScreen;
